import React, { useState, useEffect } from 'react';
import { Copy, Check, Code, Globe, FileText } from 'lucide-react';
import apiClient from '../services/apiClient';
import { useSubscription } from '../hooks/useSubscription';
import UpgradeModal from '../components/ui/UpgradeModal';

const IntegrationPage = () => {
  const [copied, setCopied] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [credentials, setCredentials] = useState({
    apiKey: '',
    companyId: '',
    widgetId: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const { hasActiveSubscription, canAccessFeature } = useSubscription();

  // Load user credentials on mount
  useEffect(() => {
    const loadCredentials = async () => {
      if (!canAccessFeature('integration')) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await apiClient.get('/api/integration/credentials');
        if (response.success && response.data) {
          setCredentials(response.data);
        }
      } catch (error) {
        // Keep empty credentials if API fails
        setCredentials({
          apiKey: '',
          companyId: '',
          widgetId: ''
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadCredentials();
  }, [canAccessFeature]);

  const embedCode = `<script src="https://pbapi.paperbrainz.com/widget.js" data-widget-id="${credentials.widgetId}"></script>`;

  const copyToClipboard = async () => {
    if (!canAccessFeature('integration')) {
      setShowUpgradeModal(true);
      return;
    }

    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Copy failed silently
    }
  };

  // Show locked state if no subscription
  if (!canAccessFeature('integration')) {
    return (
      <div className="flex items-center justify-center h-full min-h-[60vh]">
        <div className="text-center max-w-sm mx-auto p-6">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-600 rounded-[14px] flex items-center justify-center mx-auto mb-4 sm:mb-6">
            <Code className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Integration code locked
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6 leading-relaxed">
            Upgrade to generate integration code and embed your AI chatbot on your website.
          </p>
          <button
            onClick={() => setShowUpgradeModal(true)}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 sm:py-2.5 px-3 sm:px-4 rounded-full text-xs sm:text-sm transition-colors btn-touch"
          >
            View plans
          </button>
        </div>
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature="integration"
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 py-24">
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-gray-300 dark:border-white/[0.1] border-t-black dark:border-t-white rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-4 sm:py-6 md:py-8 w-full mx-auto relative">
      <div className="mb-6 sm:mb-8 md:mb-12">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-light tracking-tight text-gray-900 dark:text-white mb-2 sm:mb-3">
          Website Integration
        </h1>
        <p className="text-sm sm:text-base md:text-lg text-gray-500 dark:text-gray-500 font-light max-w-2xl">Copy and paste one line of code to add the chatbot to your website and start helping your customers instantly.</p>
      </div>

      <div className="space-y-6 sm:space-y-8 md:space-y-10">
        {/* Integration Code */}
        <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06]">
          <div className="px-5 sm:px-6 md:px-8 py-4 sm:py-5 border-b border-gray-100 dark:border-white/[0.06]">
            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 rounded-[10px] flex items-center justify-center bg-gray-50 dark:bg-white/[0.04] flex-shrink-0">
                <Code className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white">Integration Code</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 font-light mt-0.5 sm:mt-1">Add this script tag to your website to enable the chatbot</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-6 md:p-8">
            <div className="bg-black rounded-[16px] overflow-hidden">
              <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 bg-[#1a1a1a] border-b border-white/[0.06]">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full"></div>
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                </div>
                <button
                  onClick={copyToClipboard}
                  className={`p-1.5 rounded-[8px] transition-all duration-150 ${
                    copied
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-white/[0.06] hover:bg-white/[0.1] text-gray-300'
                  }`}
                  title={copied ? 'Copied!' : 'Copy to clipboard'}
                >
                  {copied ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
              <div className="p-4 sm:p-5 md:p-6">
                <code className="text-gray-300 font-mono text-xs sm:text-sm leading-5 sm:leading-6 block break-all">
                  {embedCode}
                </code>
              </div>
            </div>
          </div>
        </div>

        {/* Installation Instructions */}
        <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06]">
          <div className="px-5 sm:px-6 md:px-8 py-4 sm:py-5 border-b border-gray-100 dark:border-white/[0.06]">
            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 rounded-[10px] flex items-center justify-center bg-gray-50 dark:bg-white/[0.04] flex-shrink-0">
                <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white">Installation Instructions</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 font-light mt-0.5 sm:mt-1">Where and how to add the integration code to your website</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8">
              <div className="rounded-[16px] bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] p-4 sm:p-5 md:p-6">
                <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-3 sm:mb-4">Where to add it</h4>
                <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-500 dark:text-gray-500">
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                    <span>Before the closing &lt;/body&gt; tag</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                    <span>In your website's footer section</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                    <span>Using your website builder's HTML widget</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-[16px] bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] p-4 sm:p-5 md:p-6">
                <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-3 sm:mb-4">What happens next</h4>
                <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-gray-500 dark:text-gray-500">
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                    <span>Chatbot appears instantly on your site</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                    <span>Uses your appearance settings automatically</span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                    <span>Works on all devices and browsers</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06]">
          <div className="px-5 sm:px-6 md:px-8 py-4 sm:py-5 border-b border-gray-100 dark:border-white/[0.06]">
            <div className="flex items-start sm:items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 rounded-[10px] flex items-center justify-center bg-gray-50 dark:bg-white/[0.04] flex-shrink-0">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white">Next Steps</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 font-light mt-0.5 sm:mt-1">Complete your setup by configuring these essential features</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6">
              <div className="rounded-[16px] bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] p-4 sm:p-5 md:p-6">
                <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-1.5 sm:mb-2">Upload Knowledge Base</h4>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 mb-3 sm:mb-4">Add documents to train your AI assistant</p>
                <a
                  href="/dashboard/sources"
                  className="inline-flex items-center justify-center w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-full bg-black dark:bg-white text-white dark:text-black text-xs sm:text-sm font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors duration-150 btn-touch"
                >
                  Upload Sources
                </a>
              </div>

              <div className="rounded-[16px] bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] p-4 sm:p-5 md:p-6">
                <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-1.5 sm:mb-2">Customize Appearance</h4>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 mb-3 sm:mb-4">Match your brand colors and style</p>
                <a
                  href="/dashboard/appearance"
                  className="inline-flex items-center justify-center w-full px-3 sm:px-4 py-2 sm:py-2.5 rounded-full bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white text-xs sm:text-sm font-medium hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors duration-150 btn-touch"
                >
                  Customize
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="integration"
      />
    </div>
  );
};

export default IntegrationPage;
